import useGustoCompanyId from 'components/domain/integration/gusto/hooks/company/useGustoCompanyId/useGustoCompanyId';
import useGustoListEmployees from 'components/domain/integration/gusto/hooks/employee/useGustoListEmployees/useGustoListEmployees';
import { IGustoEmployee } from 'components/domain/integration/gusto/types/types';
import gql from 'graphql-tag';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import useMemberUpdate from 'hooks/models/member/useMemberUpdate';
import { useOrganizationSettings } from 'hooks/models/organization/settings/useOrganizationSettings/useOrganizationSettings';
import useOrganizationSettingsGlobalGustoUpdate from 'hooks/models/organization/settings/useOrganizationSettingsGlobalGustoUpdate/useOrganizationSettingsGlobalGustoUpdate';
import { keyBy, toNumber } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { Nullable } from 'types/util/Nullable';
import { mapNotNull } from 'utils/collectionUtils';
import { isoTimeStampUtc } from 'utils/dateUtils';
import { isNumberString } from 'utils/numberUtils';
import { GustoMembersIdQuery } from '__generated__/graphql';

export function useGustoMemberIdMigrationEffect() {
  const companyId = useGustoCompanyId();
  const migrate = useGustoMemberIdMigration();
  const hasStartedMigration = useRef(false);

  useEffect(() => {
    if (companyId && hasStartedMigration.current === false) {
      hasStartedMigration.current = true;
      migrate();
    }
  }, [companyId, migrate]);
}

export default function useGustoMemberIdMigration() {
  const listGustoEmployees = useGustoListEmployees();
  const companyId = useGustoCompanyId();
  const paging = useGraphQLPaging();
  const updateGustoSetting = useOrganizationSettingsGlobalGustoUpdate();
  const [updateMemberKey] = useMemberUpdate();
  const isMigrated = useOrganizationSettings()?.global?.payroll?.gusto?.isMigratedToMemberUuidsTimeStamp;

  return useCallback(async () => {
    if (!companyId || isMigrated) {
      return null;
    }

    const gustoMembers = await listGustoEmployees(companyId);
    const busyMembers = await paging<GustoMembersIdQuery>({ document: GUSTO_MEMBER_QUERY }, 'members');

    const migrationStructure = parseMembersForGustoIdMigration(busyMembers, gustoMembers);
    const promises = migrationStructure.map(async ({ busyId, gustoUuid }) =>
      updateMemberKey(busyId, 'gustoEmployeeId', gustoUuid)
    );

    await Promise.all(promises);
    updateGustoSetting('isMigratedToMemberUuidsTimeStamp', isoTimeStampUtc());

    return true;
  }, [companyId, isMigrated, listGustoEmployees, paging, updateGustoSetting, updateMemberKey]);
}

export type BusyMemberGustoMigrationIdRequirements = Array<
  Omit<NonNullArrayField<GustoMembersIdQuery['members']>, 'cursor'>
>;

export function parseMembersForGustoIdMigration(
  busyMembers: BusyMemberGustoMigrationIdRequirements,
  gustoMembers: Array<Pick<IGustoEmployee, 'id' | 'uuid'>>
): Array<{ busyId: string; gustoUuid: Nullable<string> }> {
  const gustoMemberIdMap = keyBy(gustoMembers, (gustoMember) => gustoMember.id);

  return mapNotNull(busyMembers, (member) => {
    const shouldGustoEmployeeIdBeMigrated = member.gustoEmployeeId && isNumberString(member.gustoEmployeeId);
    if (!shouldGustoEmployeeIdBeMigrated) {
      return null;
    }

    // Could happen if the link was somehow still there despite the member being deleted on gusto, we can clear the link here
    const gustoMember = gustoMemberIdMap[toNumber(member.gustoEmployeeId)];
    if (!gustoMember) {
      return {
        busyId: member.id,
        gustoUuid: null,
      };
    }

    return {
      busyId: member.id,
      gustoUuid: gustoMember.uuid,
    };
  });
}

export const GUSTO_MEMBER_QUERY = gql`
  query GustoMembersId($first: Int, $after: String, $filter: MemberFilter, $sort: [MemberSort!]) {
    members(first: $first, after: $after, filter: $filter, sort: $sort) {
      id
      cursor
      gustoEmployeeId
    }
  }
`;
